import { useEffect, useState } from "react";

import { HelpdeskContactIcon } from "./HelpdeskContactIcon";
import { useHelpdeskPublicCopy } from "./helpdeskPublicCatalog";

type Profile = {
  version: number;
  signal_label: string;
  signal_url: string;
  whatsapp_label: string;
  whatsapp_url: string;
  telegram_label: string;
  telegram_url: string;
  email: string;
  pgp_fingerprint: string;
  pgp_public_key_sha256: string;
  pgp_public_key_url: string;
  pgp_created_at: string;
  pgp_expires_at: string | null;
  pgp_key_state: "valid" | "expiring" | "expired" | "no_expiry";
  expiry_warning_days: number;
  updated_at: string;
  pgp_public_key_armor: null;
  logo_url: string | null;
};

type LoadState =
  | { state: "loading" }
  | { state: "ready"; profile: Profile }
  | { state: "unavailable" }
  | { state: "failed" };

function mailto(value: string): string {
  return `mailto:${encodeURIComponent(value).replace("%40", "@")}`;
}

export function HelpdeskPublicPage() {
  const { t } = useHelpdeskPublicCopy();
  const [load, setLoad] = useState<LoadState>({ state: "loading" });
  const [logoAttempt, setLogoAttempt] = useState<0 | 1 | 2>(0);

  useEffect(() => {
    const controller = new AbortController();
    void fetch("/api/v1/public/helpdesk", {
      signal: controller.signal,
      credentials: "same-origin",
      cache: "no-store",
      headers: { Accept: "application/json" },
    })
      .then(async (response) => {
        if (response.status === 404) {
          setLoad({ state: "unavailable" });
          return;
        }
        if (!response.ok) throw new Error("helpdesk profile unavailable");
        setLoad({ state: "ready", profile: await response.json() as Profile });
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        setLoad({ state: "failed" });
      });
    return () => controller.abort();
  }, []);

  const profile = load.state === "ready" ? load.profile : null;
  const logoSource = load.state === "loading" ? null : logoAttempt === 0
    ? profile?.logo_url ?? "/api/v1/public/helpdesk/logo"
    : logoAttempt === 1
      ? "/api/v1/public/helpdesk/logo"
      : null;


  return <main className="helpdesk-page" data-helpdesk-public-page data-public-foundation-control="true">
    <section className="helpdesk-hero">
      <div className="shell helpdesk-hero-grid">
        <div className="helpdesk-logo-panel" aria-label={t.logoAria}>
          <div className={"helpdesk-brand-lockup " + (logoSource ? "has-custom-logo" : "is-fallback")}>
            <span className="helpdesk-brand-logo">
              {logoSource
                ? <img src={logoSource} alt="" onError={() => setLogoAttempt((current) => current === 0 && profile?.logo_url ? 1 : 2)} />
                : <span className="helpdesk-brand-fallback">M</span>}
            </span>
            {!logoSource ? <span className="helpdesk-brand-copy">
              <strong>{t.wordmark}</strong>
              <small>{t.byMiaan}</small>
            </span> : null}
          </div>
        </div>
        <div className="helpdesk-hero-copy">
          <span className="helpdesk-kicker"><i />{t.available}</span>
          <h1>{t.heroTitle}</h1><p>{t.heroBody}</p>
          <div className="urgent-note"><strong>{t.urgentTitle}</strong><span>{t.urgentBody}</span></div>
        </div>
      </div>
    </section>

    <section className="contact-section shell">
      <div className="section-heading"><span className="eyebrow">{t.contactEyebrow}</span><h2>{t.contactTitle}</h2><p>{t.contactBody}</p></div>
      {load.state === "loading" ? <div className="helpdesk-public-state" role="status">{t.loading}</div>
        : load.state === "unavailable" ? <div className="helpdesk-public-state" role="status"><strong>{t.unavailableTitle}</strong><span>{t.unavailableBody}</span></div>
        : load.state === "failed" ? <div className="helpdesk-public-state" role="alert"><strong>{t.loadFailed}</strong><span>{t.unavailableBody}</span></div> : null}
      {profile ? <div className="contact-grid">
        <article className="contact-card primary-contact"><HelpdeskContactIcon channel="signal" /><span>{t.recommended}</span><h3>Signal</h3><a data-helpdesk-contact data-kavoosh-technical href={profile.signal_url} target="_blank" rel="noreferrer">{profile.signal_label}</a><p>{t.signalBody}</p><a className="button helpdesk-button" href={profile.signal_url} target="_blank" rel="noreferrer">{t.openSignal}</a></article>
        <article className="contact-card"><HelpdeskContactIcon channel="whatsapp" /><span>{t.messaging}</span><h3>WhatsApp</h3><a data-helpdesk-contact data-kavoosh-technical href={profile.whatsapp_url} target="_blank" rel="noreferrer">{profile.whatsapp_label}</a><p>{t.whatsappBody}</p><a className="button secondary wide" href={profile.whatsapp_url} target="_blank" rel="noreferrer">{t.openWhatsapp}</a></article>
        <article className="contact-card"><HelpdeskContactIcon channel="telegram" /><span>{t.botIntake}</span><h3>Telegram</h3><a data-helpdesk-contact data-kavoosh-technical href={profile.telegram_url} target="_blank" rel="noreferrer">{profile.telegram_label}</a><p>{t.telegramBody}</p><a className="button secondary wide" href={profile.telegram_url} target="_blank" rel="noreferrer">{t.openTelegram}</a></article>
        <article className="contact-card"><HelpdeskContactIcon channel="email" /><span>{t.emailChannel}</span><h3>{t.secureEmail}</h3><a data-helpdesk-contact data-kavoosh-technical href={mailto(profile.email)}>{profile.email}</a><p>{t.emailBody}</p><a className="button secondary wide" href={mailto(profile.email)}>{t.writeEmail}</a></article>
      </div> : null}
    </section>

    <section className="helpdesk-safety shell">
      {t.safety.map((item, index) => <article key={item.title}><span className="safety-number">{String(index + 1).padStart(2, "0")}</span><h2>{item.title}</h2><ul>{item.items.map((entry) => <li key={entry}>{entry}</li>)}</ul></article>)}
    </section>

    {profile ? <section className="pgp-section shell">
      <div className="pgp-copy"><span className="eyebrow">{t.encryptedEmail}</span><h2>{t.pgpTitle}</h2><p>{t.pgpBody}</p><div className="fingerprint"><small>{t.fingerprint}</small><code data-helpdesk-contact data-kavoosh-technical>{profile.pgp_fingerprint}</code></div>{profile.pgp_key_state === "expiring" || profile.pgp_key_state === "expired" ? <p className="pgp-key-warning" role="status">{profile.pgp_key_state === "expired" ? t.pgpExpired : t.pgpExpiring}</p> : null}</div>
      <div className="key-card"><span className="key-badge">PGP</span><h3>{t.keyTitle}</h3><p><strong>{t.emailLabel}:</strong> <span data-helpdesk-contact data-kavoosh-technical>{profile.email}</span></p><a className="button helpdesk-button wide" href={profile.pgp_public_key_url} download="helpdesk-public-key.asc">{t.downloadKey}</a><small>{t.keyNote}</small></div>
    </section> : null}

    <section className="helpdesk-boundary shell"><span>i</span><p><strong>{t.boundaryTitle}</strong> {t.boundaryBody}</p></section>
  </main>;
}
